import { memo } from 'react';
import { useTheme } from '@mui/material/styles';
import { IconButton, Toolbar, useMediaQuery, Box } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';

import AppBarStyled from './AppBarStyled';
import HeaderContent from './HeaderContent';

interface HeaderProps {
  open: boolean;
  handleDrawerToggle: () => void;
}

const Header = memo<HeaderProps>(function Header({ open, handleDrawerToggle }) {
  const theme = useTheme();
  const matchUpLG = useMediaQuery(theme.breakpoints.up('lg'));

  // common header
  const mainHeader = (
    <Toolbar
      sx={{
        minHeight: { xs: '64px', lg: '70px' },
        px: { xs: 1.5, sm: 2.5, lg: 3 },
        gap: { xs: 1, sm: 1.5 },
      }}
    >
      {/* Hamburger Menu Button */}
      <IconButton
        aria-label="toggle drawer"
        onClick={handleDrawerToggle}
        edge="start"
        sx={{
          width: 42,
          height: 42,
          borderRadius: '10px',
          bgcolor: open
            ? 'rgba(16, 185, 129, 0.2)'
            : 'rgba(255, 255, 255, 0.1)',
          color: open ? '#10b981' : '#ffffff',
          border: open
            ? '2px solid rgba(16, 185, 129, 0.3)'
            : '2px solid transparent',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: open
              ? 'rgba(16, 185, 129, 0.3)'
              : 'rgba(255, 255, 255, 0.15)',
            transform: 'scale(1.05)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          {open ? (
            <MenuOpenIcon sx={{ fontSize: '1.5rem' }} />
          ) : (
            <MenuIcon sx={{ fontSize: '1.5rem' }} />
          )}
        </Box>
      </IconButton>

      {/* Divider */}
      <Box
        sx={{
          width: '1px',
          height: '28px',
          bgcolor: 'rgba(255, 255, 255, 0.15)',
          mx: { xs: 0.5, sm: 1 },
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Header Content */}
      <Box sx={{ flexGrow: 1 }}>
        {matchUpLG && <HeaderContent />}
        {!matchUpLG && !open && <HeaderContent />}
      </Box>
    </Toolbar>
  );

  // app-bar params
  const appBar: any = {
    position: 'fixed',
    color: 'transparent',
    elevation: 0,
  };

  return (
    <AppBarStyled open={open} {...appBar}>
      {mainHeader}
    </AppBarStyled>
  );
});

Header.displayName = 'Header';

export default Header;
